import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Participants = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [participations, setParticipations] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // DEBUG: Vérifier que le composant charge
  console.log('=== Participants component loaded ===');

  // Fonction pour formater le numéro de téléphone camerounais
  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    // Supprimer tous les caractères non numériques
    const cleaned = phone.replace(/\D/g, '');
    // Si le numéro commence par 237 (indicatif Cameroun), le formater
    if (cleaned.startsWith('237') && cleaned.length === 12) {
      return `+${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6, 9)} ${cleaned.substring(9, 12)}`;
    }
    // Sinon, retourner le numéro original
    return phone;
  };

  const [filters, setFilters] = useState({
    city_id: null,
    pdv_id: null,
    session_id: null,
    played: '',
    date_from: null
  });

  const loadParticipations = async () => {
    console.log('=== loadParticipations called ===');
    console.log('Current filters:', filters);
    try {
      const params = { brand: 'reaktor' };
      if (filters.city_id) params.city_id = parseInt(filters.city_id);
      if (filters.pdv_id) params.pdv_id = parseInt(filters.pdv_id);
      if (filters.session_id) params.session_id = parseInt(filters.session_id);
      if (filters.played !== '') params.played = filters.played === 'true' ? true : false;
      if (filters.date_from) params.date_from = filters.date_from;

      console.log('Params prepared:', params);
      const response = await api.get('/admin/participations', { params });
      console.log('API Response - count:', response.data.length);
      setParticipations(response.data);
    } catch (error) {
      console.error('Error loading participations:', error);
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des participants' });
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadParticipations();
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadPdvs = async () => {
    try {
      const response = await api.get('/admin/pdv');
      setPdvs(response.data);
    } catch (error) {
      console.error('Error loading PDVs:', error);
    }
  };

  const loadCities = async () => {
    try {
      const response = await api.get('/admin/cities');
      setCities(response.data);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await api.get('/admin/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadCities();
    loadPdvs();
    loadSessions();
  }, [user, navigate]);

  // Reload PDVs when city changes
  useEffect(() => {
    if (filters.city_id) {
      loadPdvs();
    }
  }, [filters.city_id]);

  // Charger les participations quand les filtres changent
  useEffect(() => {
    console.log('=== useEffect with filters triggered, filters:', filters);
    loadParticipations();
  }, [filters]);

  const handleSendWhatsApp = async () => {
    if (!selectedParticipation || !whatsappMessage.trim()) {
      setMessage({ type: 'danger', text: 'Veuillez entrer un message' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('message', whatsappMessage);

      const response = await api.post(`/admin/send-whatsapp/${selectedParticipation.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Ouvrir le lien WhatsApp dans un nouvel onglet
      window.open(response.data.whatsapp_url, '_blank');

      setMessage({ type: 'success', text: 'Lien WhatsApp ouvert !' });
      setShowWhatsAppModal(false);
      setWhatsappMessage('');
      setSelectedParticipation(null);
    } catch (error) {
      setMessage({ type: 'danger', text: error.response?.data?.detail || 'Erreur lors de l\'envoi' });
    }
  };

  const handleViewDetails = async (participation) => {
    try {
      const response = await api.get(`/admin/participations/${participation.id}`);
      setSelectedParticipation(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Erreur lors du chargement des détails' });
    }
  };

  const getPrizeLabel = (prizeType) => {
    const labels = {
      'passion': 'Rose',
      'force': 'Bouteille',
      'complicite': 'Chocolats',
      'tendresse': 'Polo',
      'amitie': 'Dîner',
      'aventure': 'Week-end Kribi',
      'try_again_1': 'Try Again - Réessayez',
      'try_again_2': 'Try Again - Réessayez'
    };
    return labels[prizeType] || prizeType;
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="spinner-border text-warning" role="status"></div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
      {/* Header */}
      <nav className="navbar navbar-expand-lg navbar-dark sticky-top" style={{ background: 'rgba(26, 26, 46, 0.95)', padding: '12px 0' }}>
        <div className="container">
          <a className="navbar-brand fw-bold d-flex align-items-center" href="/dashboard" style={{ fontSize: '20px' }}>
            <div style={{ width: '40px', height: '40px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '12px' }}>
              <i className="fas fa-bolt text-dark" style={{ fontSize: '18px' }}></i>
            </div>
            <span>REAKTOR Admin</span>
          </a>
          <div className="d-flex align-items-center">
            <span className="text-white me-3">
              <i className="fas fa-user-shield me-2"></i>{user?.username}
            </span>
            <a href="/dashboard" className="btn btn-outline-light btn-sm">
              <i className="fas fa-arrow-left me-2"></i>Retour
            </a>
          </div>
        </div>
      </nav>

      <div className="container py-4">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show mb-3`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })}></button>
          </div>
        )}

        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="text-white fw-bold mb-1">
              <i className="fas fa-users me-2" style={{ color: '#FFD700' }}></i>
              Gestion des Participants
            </h2>
            <p className="text-light mb-0">{participations.length} participant(s)</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow mb-4" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-md-2">
                <label className="form-label text-light small">Ville</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.city_id}
                  onChange={(e) => setFilters({ ...filters, city_id: e.target.value, pdv_id: '' })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes les villes</option>
                  {cities.map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">PDV</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.pdv_id}
                  onChange={(e) => setFilters({ ...filters, pdv_id: e.target.value })}
                  disabled={!filters.city_id}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">{filters.city_id ? 'Sélectionner un PDV' : 'Tous'}</option>
                  {pdvs.filter(p => !filters.city_id || p.city_id == filters.city_id).map(pdv => (
                    <option key={pdv.id} value={pdv.id}>{pdv.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Session</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.session_id}
                  onChange={(e) => setFilters({ ...filters, session_id: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Toutes</option>
                  {sessions.map(session => (
                    <option key={session.id} value={session.id}>{session.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Statut jeu</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.played}
                  onChange={(e) => setFilters({ ...filters, played: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                >
                  <option value="">Tous</option>
                  <option value="false">En attente</option>
                  <option value="true">Joués</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label text-light small">Date début</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderColor: 'rgba(255, 215, 0, 0.3)', color: '#fff' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Participants Table */}
        <div className="card border-0 shadow" style={{ borderRadius: '12px', background: 'rgba(26, 26, 46, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead style={{ background: 'rgba(255, 215, 0, 0.1)' }}>
                  <tr>
                    <th className="text-light p-3">Date/Heure</th>
                    <th className="text-light p-3">Participant</th>
                    <th className="text-light p-3">Contact</th>
                    <th className="text-light p-3">PDV</th>
                    <th className="text-light p-3">Hôtesse</th>
                    <th className="text-light p-3">Mode</th>
                    <th className="text-light p-3">Statut</th>
                    <th className="text-light p-3">Lot</th>
                    <th className="text-light p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {participations.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="fas fa-users-slash fa-3x mb-3" style={{ opacity: 0.3, color: '#FFD700' }}></i>
                        <p className="text-light mb-0">Aucun participant trouvé</p>
                      </td>
                    </tr>
                  ) : participations.map((p, idx) => (
                    <tr key={p.id} style={{ color: '#fff', cursor: 'pointer', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'transparent' }} onClick={() => handleViewDetails(p)}>
                      <td className="p-3">
                        <div style={{ fontSize: '12px', color: '#333333', fontWeight: 600, marginBottom: '2px' }}>{new Date(p.created_at).toLocaleDateString('fr-FR')}</div>
                        <div style={{ fontSize: '13px', color: '#333333', fontWeight: 600 }}>{new Date(p.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="p-3 fw-semibold">{p.participant_name}</td>
                      <td className="p-3 font-monospace">{formatPhoneNumber(p.participant_phone)}</td>
                      <td className="p-3">{p.pdv_name}</td>
                      <td className="p-3">{p.hotesse_name}</td>
                      <td className="p-3">
                        <span className={`badge ${p.play_mode === 'whatsapp' ? 'bg-success' : 'bg-primary'}`}>
                          <i className={`fab fa-${p.play_mode === 'whatsapp' ? 'whatsapp' : 'chrome'} me-1`}></i>
                          {p.play_mode === 'whatsapp' ? 'WhatsApp' : 'Direct'}
                        </span>
                      </td>
                      <td className="p-3">
                        {p.played ? (
                          <span className="badge bg-success">
                            <i className="fas fa-check-circle me-1"></i>Joué
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            <i className="fas fa-clock me-1"></i>En attente
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        {p.plays_history && p.plays_history.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {p.plays_history.map((play, idx) => (
                              <span key={idx} className="badge bg-info" style={{ fontSize: '11px' }}>
                                Tour {play.play_number}: {getPrizeLabel(play.prize_type)}
                              </span>
                            ))}
                          </div>
                        ) : p.prize_won ? (
                          <span className="badge bg-info">{getPrizeLabel(p.prize_won)}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-sm btn-success me-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedParticipation(p);
                            setShowWhatsAppModal(true);
                          }}
                          title="Envoyer WhatsApp"
                        >
                          <i className="fab fa-whatsapp"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(p);
                          }}
                          title="Voir détails"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsAppModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fab fa-whatsapp text-success me-2"></i>
                  Envoyer message WhatsApp
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowWhatsAppModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label text-light">Participant</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_name}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Téléphone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedParticipation.participant_phone}
                    disabled
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label text-light">Message</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={whatsappMessage}
                    onChange={(e) => setWhatsappMessage(e.target.value)}
                    placeholder="Entrez votre message..."
                    style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,215,0,0.3)', color: '#fff' }}
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWhatsAppModal(false)}>Annuler</button>
                <button type="button" className="btn btn-success" onClick={handleSendWhatsApp}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedParticipation && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: '#1a1a2e', border: '2px solid #FFD700' }}>
              <div className="modal-header border-bottom-0">
                <h5 className="modal-title text-white">
                  <i className="fas fa-user me-2" style={{ color: '#FFD700' }}></i>
                  Détails du participant
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Nom</label>
                    <div className="text-white fw-semibold">{selectedParticipation.participant?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Téléphone</label>
                    <div className="text-white font-monospace">{formatPhoneNumber(selectedParticipation.participant?.phone)}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">PDV</label>
                    <div className="text-white">{selectedParticipation.pdv?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Hôtesse</label>
                    <div className="text-white">{selectedParticipation.hotesse?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Session</label>
                    <div className="text-white">{selectedParticipation.session?.name}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Mode de jeu</label>
                    <div className="text-white">{selectedParticipation.play_mode === 'whatsapp' ? 'WhatsApp' : 'Direct'}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Date d'enregistrement</label>
                    <div className="text-white">{new Date(selectedParticipation.created_at).toLocaleString('fr-FR')}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label text-light small">Nombre de bouteilles</label>
                    <div className="text-white">{selectedParticipation.bottles_count}</div>
                  </div>
                  {selectedParticipation.played && (
                    <>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Date du jeu</label>
                        <div className="text-white">{selectedParticipation.played_at ? new Date(selectedParticipation.played_at).toLocaleString('fr-FR') : '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Lot gagné</label>
                        <div className="text-white">{selectedParticipation.prize_label || '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Code lot</label>
                        <div className="text-white font-monospace">{selectedParticipation.prize_code || '-'}</div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label text-light small">Statut livraison</label>
                        <div className="text-white">
                          {selectedParticipation.prize_delivered ? (
                            <span className="badge bg-success">Livré</span>
                          ) : (
                            <span className="badge bg-secondary">Non livré</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {selectedParticipation.ticket_photo_url && (
                    <div className="col-12 mb-3">
                      <label className="form-label text-warning fw-bold">
                        <i className="fas fa-receipt me-2"></i>Photo du ticket de caisse
                      </label>
                      <div className="p-3 rounded" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                        <a href={selectedParticipation.ticket_photo_url} target="_blank" rel="noopener noreferrer">
                          <img
                            src={selectedParticipation.ticket_photo_url}
                            alt="Ticket de caisse"
                            style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px', cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'block';
                            }}
                          />
                          <div style={{ display: 'none' }} className="text-center text-warning mt-2">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Image non disponible
                          </div>
                        </a>
                        <div className="text-center mt-2">
                          <small className="text-light">Cliquez sur l'image pour l'agrandir</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button type="button" className="btn btn-primary" onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedParticipation(p => ({ ...p, participant: selectedParticipation.participant }));
                  setShowWhatsAppModal(true);
                }}>
                  <i className="fab fa-whatsapp me-2"></i>Envoyer WhatsApp
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Participants;
// FORCE REBUILD 1770262325
// TEST 1770264153
// Force rebuild 1770278920
// Force rebuild - 1770281969
